import React, { useEffect, useState } from "react";
import "./ProducerDetail.scss";
import { useSearchParams } from "react-router-dom";
import Helpers from "@utils/Helpers";
import { errorLog } from "logging";
import userApi from "services/UserApi";
import type { FeedbackOut, RatingSummary } from "services/FeedbackApi";
import {
  Avatar,
  Button,
  Carousel,
  Divider,
  Empty,
  Image,
  Pagination,
  Rate,
  Spin,
} from "antd";
import contact from "@assets/icons/contact.svg";
import location from "@assets/icons/location-marker.svg";
import globe from "@assets/icons/globe-alt.svg";
import size from "@assets/icons/Size.svg";
import ProducerFilter from "@containers/Producer/ProducerFilter/ProducerFilter";
import { IProductSearch } from "@utils/Interfaces";
import ProductItem from "@components/ProductItem/ProductItem";
import { Link } from "react-router-dom";

const FeedbackItem: React.FC<FeedbackOut> = ({
  consumer_name,
  avatar_url,
  rating,
  comment,
  created_at,
  images,
}) => {
  return (
    <div className="feedback-item">
      <div className="customer">
        <div className="customer-avatar">
          <Avatar size={70} src={avatar_url || undefined}>
            {consumer_name ? consumer_name.charAt(0).toUpperCase() : "?"}
          </Avatar>
        </div>
        <div className="customer-info">
          <span className="customer-name">{consumer_name || "Anonymous"}</span>
          <Rate disabled value={rating} className="rate" />
          <span className="feedback-date">
            {created_at ? Helpers.formatDateTime(created_at) : ""}
          </span>
        </div>
      </div>
      <div className="feedback-content">
        {comment}
        {images.length > 0 && (
          <div className="image-group">
            {images.map((url, index) => {
              return <Image key={index} className="image-item" src={url} />;
            })}
          </div>
        )}
      </div>
    </div>
  );
};

const ProducerDetail = () => {
  const [activeTab, setActiveTab] = useState(1);
  const [activeRating, setActiveRating] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const producerId = searchParams.get("producerId");
  const [isFetch, setIsFetch] = useState(false);
  const [producer, setProducer] = useState<any>(null);
  const [feedbackSummary, setFeedbackSummary] =
    useState<RatingSummary | null>(null);
  const [feedbackList, setFeedbackList] = useState<FeedbackOut[]>([]);
  const [feedbackTotal, setFeedbackTotal] = useState(0);
  const [feedbackPage, setFeedbackPage] = useState(1);
  const [isFeedbackLoading, setIsFeedbackLoading] = useState(false);
  const feedbackPageSize = 10;

  const [searchParam, setSearchParam] = useState<IProductSearch>({
    query: "",
    type: null,
    location: null,
    categories_id: [1, 2, 3],
    selling_method: null,
    quantity: null,
    radius: 300,
    address: "",
    filters: {
      hay_cut: [],
      bale_weight_min: 0,
      bale_weight_max: 200,
      shape: [],
      size: [],
      hay_compression: [],
      moisture: [],
      rain: [],
      storage: [],
      crop: [],
      binding_type: [],
      price_min: 0,
      price_max: 1000,
      shipping_options: [],
      payment_options: [],
      crude_protein_range_ids: [],
    },
  });
  const [isOpenMap, setIsOpenMap] = useState(false);
  const [viewMode, setViewMode] = useState("small");

  // fetch producer detail
  useEffect(() => {
    const fetchProducer = async () => {
      try {
        const response = await userApi.getProducerDetail(parseInt(producerId!));
        if (response.status == 200) {
          setProducer(response.data.data);
        }
      } catch (error) {
        Helpers.notification.error("Can't fetch producer. Please try again");
        errorLog("ProducerDetail.tsx - fetchProducer", error);
      }
    };
    if (!isFetch && producerId) {
      fetchProducer();
      setIsFetch(true);
    }
  }, [isFetch, producerId]);

  // fetch producer feedback summary (head rating + pill counts)
  useEffect(() => {
    const fetchFeedbackSummary = async () => {
      if (!producerId) {
        return;
      }
      try {
        const response = await userApi.getProducerFeedbackSummary(
          parseInt(producerId)
        );
        if (response.status == 200) {
          setFeedbackSummary(response.data);
        }
      } catch (error) {
        errorLog("ProducerDetail.tsx - fetchFeedbackSummary", error);
      }
    };
    fetchFeedbackSummary();
  }, [producerId]);

  // fetch paginated producer feedback list (filtered by star)
  useEffect(() => {
    const fetchFeedback = async () => {
      if (!producerId) {
        return;
      }
      setIsFeedbackLoading(true);
      try {
        const response = await userApi.getProducerFeedback(
          parseInt(producerId),
          {
            star: activeRating || undefined,
            page: feedbackPage,
            page_size: feedbackPageSize,
          }
        );
        if (response.status == 200) {
          setFeedbackList(response.data.items ?? []);
          setFeedbackTotal(response.data.total ?? 0);
        }
      } catch (error) {
        errorLog("ProducerDetail.tsx - fetchFeedback", error);
        setFeedbackList([]);
        setFeedbackTotal(0);
      } finally {
        setIsFeedbackLoading(false);
      }
    };
    fetchFeedback();
  }, [producerId, activeRating, feedbackPage, feedbackPageSize]);

  const selectRating = (star: number) => {
    setFeedbackPage(1);
    setActiveRating(star);
  };

  const menuBtn = [
    {
      id: 1,
      title: "Shop",
    },
    {
      id: 2,
      title: "About",
    },
    {
      id: 3,
      title: "Feedback",
    },
  ];

  const ratingGroup = [
    {
      id: 0,
      title: `All (${feedbackSummary?.total ?? 0})`,
    },
    {
      id: 1,
      title: `1 Star (${feedbackSummary?.by_star?.[1] ?? 0})`,
    },
    {
      id: 2,
      title: `2 Stars (${feedbackSummary?.by_star?.[2] ?? 0})`,
    },
    {
      id: 3,
      title: `3 Stars (${feedbackSummary?.by_star?.[3] ?? 0})`,
    },
    {
      id: 4,
      title: `4 Stars (${feedbackSummary?.by_star?.[4] ?? 0})`,
    },
    {
      id: 5,
      title: `5 Stars (${feedbackSummary?.by_star?.[5] ?? 0})`,
    },
  ];

  const detailRatings = [
    { label: "Quality", value: feedbackSummary?.detail.quality ?? 0 },
    { label: "Shipping Cost", value: feedbackSummary?.detail.shipping_cost ?? 0 },
    { label: "Shipping Time", value: feedbackSummary?.detail.shipping_time ?? 0 },
    { label: "Service", value: feedbackSummary?.detail.service ?? 0 },
  ];

  return (
    <div className="ProducerDetail">
      <div className="producer-head">
        <div className="producer-banner">
          {producer?.producer_farm_profile.cover ? (
            <img src={producer?.producer_farm_profile.cover} alt="banner" />
          ) : (
            <div
              style={{
                width: "100%",
                height: "259.04px",
                backgroundColor: "#cecece",
              }}
            ></div>
          )}
        </div>
        <div className="container">
          <div className="avatar-wrap-position">
            <div className="avatar-wrap">
              {producer?.producer_farm_profile.avatar ||
              producer?.producer_farm_profile.avatar != "" ? (
                <Avatar
                  src={producer?.producer_farm_profile.avatar}
                  size={180}
                  className="avatar"
                />
              ) : (
                <Avatar className="farm-avatar-default">
                  {producer?.producer_farm_profile.name
                    ? producer?.producer_farm_profile.name[0].toUpperCase()
                    : "F"}
                </Avatar>
              )}
            </div>
            <div className="main-head">
              <div className="farm-contact">
                <div className="farm-name">
                  {producer?.producer_farm_profile.name}
                  <span className="farm-rank">#9 on leaderboard </span>
                </div>
                <Button className="contact-btn">
                  <img
                    src={contact}
                    alt="contact"
                    width={20}
                    className="contact-icon"
                  />
                  Contact Producer
                </Button>
              </div>
              <div className="farm-rating">
                <Rate
                  disabled
                  value={feedbackSummary?.avg_rating ?? 0}
                  className="rate"
                />{" "}
                {(feedbackSummary?.avg_rating ?? 0).toFixed(1)}{" "}
                <span className="review">
                  ({feedbackSummary?.total ?? 0} reviews)
                </span>
              </div>
              <div className="farm-info">
                <div className="farm-info-item">
                  <img src={size} alt="size" width={20} />
                  <span>{producer?.producer_farm_profile.farm_size} acres</span>
                </div>
                <div className="farm-info-item">
                  <img src={location} alt="location" width={20} />
                  <span>
                    {producer?.producer.address.province}{" "}
                    {producer?.producer.address.address_line},{" "}
                    {producer?.producer.address.city}{" "}
                    {producer?.producer.address.postal_code}
                  </span>
                </div>
                {producer?.producer_farm_profile.website && (
                  <div className="farm-info-item">
                    <img src={globe} alt="web" width={20} />
                    <Link to={producer?.producer_farm_profile.website}>
                      {producer?.producer_farm_profile.website}
                    </Link>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="producer-body">
        <div className="container">
          <div className="producer-menu">
            {menuBtn.map((item: any, index: number) => {
              return (
                <Button
                  key={index}
                  className={`tab-btn ${activeTab == item.id ? "active" : ""}`}
                  onClick={() => setActiveTab(item.id)}
                >
                  {item.title}
                </Button>
              );
            })}
          </div>
          <div className="producer-content">
            <ProducerFilter
              searchParam={searchParam}
              setSearchParam={setSearchParam}
              setIsOpenMap={setIsOpenMap}
              isOpenMap={isOpenMap}
              componentIn={"productCatalog"}
              viewMode={viewMode}
              setViewMode={setViewMode}
            />

            {activeTab === 1 && (
              <div className="product-group">
                <div className="product-item">
                  <div className="section-title">Top Picks</div>
                  {producer?.products.length === 0 ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  ) : (
                    <div className="product-wrap">
                      {producer?.products
                        .slice(0, 5)
                        .map((item: any, index: number) => {
                          return (
                            <div key={index}>
                              <ProductItem
                                data={item}
                                isAuction={false}
                                type=""
                                index={index}
                              />
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
                <div className="product-item">
                  <div className="section-title">Newly Listed</div>
                  {producer?.products.length === 0 ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  ) : (
                    <div className="product-wrap">
                      {producer?.products
                        .slice(0, 5)
                        .map((item: any, index: number) => {
                          return (
                            <div key={index}>
                              <ProductItem
                                data={item}
                                isAuction={false}
                                type=""
                                index={index}
                              />
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
                <div className="product-item">
                  <div className="section-title">All Items</div>
                  {producer?.products.length === 0 ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  ) : (
                    <div className="product-wrap">
                      {producer?.products
                        .slice(0, 5)
                        .map((item: any, index: number) => {
                          return (
                            <div key={index}>
                              <ProductItem
                                data={item}
                                isAuction={false}
                                type=""
                                index={index}
                              />
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              </div>
            )}

            {activeTab === 2 && (
              <div className="about">
                <div className="farm-bio">
                  <div className="section-title">Bio</div>
                  {producer?.producer_farm_profile.farm_bio ? (
                    producer?.producer_farm_profile.farm_bio
                  ) : (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  )}
                </div>
                <div className="farm-image">
                  <Carousel
                    autoplay
                    autoplaySpeed={3000}
                    arrows
                    dots={true}
                    style={{ width: 400 }}
                    className="image-group"
                  >
                    {producer?.producer_farm_profile?.farm_images?.map(
                      (item: any, index: number) => {
                        return (
                          <Image
                            key={index}
                            width={"100%"}
                            className="image-item"
                            src={item}
                          />
                        );
                      }
                    )}
                  </Carousel>
                </div>
              </div>
            )}

            {activeTab === 3 && (
              <div className="feedback">
                <div className="rating-group">
                  <div className="rating-group-btn">
                    {ratingGroup.map((item: any, index: number) => {
                      return (
                        <Button
                          key={index}
                          className={`tab-btn ${
                            activeRating == item.id ? "active" : ""
                          }`}
                          onClick={() => selectRating(item.id)}
                        >
                          {item.title}
                        </Button>
                      );
                    })}
                  </div>
                  <div className="section-subtitle">Detail Ratings:</div>
                  {detailRatings.map((item) => {
                    return (
                      <div className="rating-item" key={item.label}>
                        <span>{item.label}</span>
                        <span>
                          <Rate disabled value={item.value} className="rate" />{" "}
                          {item.value.toFixed(1)}
                        </span>
                      </div>
                    );
                  })}
                </div>
                <div className="feedback-group">
                  {isFeedbackLoading ? (
                    <Spin />
                  ) : feedbackList.length === 0 ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  ) : (
                    feedbackList.map((item, index) => {
                      return (
                        <React.Fragment key={item.feedback_id}>
                          {index > 0 && <Divider />}
                          <FeedbackItem {...item} />
                        </React.Fragment>
                      );
                    })
                  )}

                  {feedbackTotal > 0 && (
                    <Pagination
                      className="pagination"
                      align="end"
                      current={feedbackPage}
                      pageSize={feedbackPageSize}
                      total={feedbackTotal}
                      onChange={(page) => setFeedbackPage(page)}
                    />
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProducerDetail;
